import {RuntimeContext} from "../runtime/RuntimeContext";
import {RuntimeValue} from "../runtime/RuntimeValue";
import {IfExpression} from "./IfExpression";
import {DynamicFormsExpression} from "./DynamicFormsExpression";
import {LiteralExpression} from "./LiteralExpression";
import {ReferenceExpression} from "./ReferenceExpression";
import {RuntimeString} from "../runtime/RuntimeString";
import {Configuration} from "../../../../global/Configuration";
import {AllExpression} from "./AllExpression";
import {NotExpression} from "./NotExpression";
import {TodayExpression} from "./TodayExpression";
import {StringJoinExpression} from "./StringJoinExpression";
import {RuntimeBoolean} from "../runtime/RuntimeBoolean";
import {LocalDate} from "@js-joda/core";
import {GreaterThanOrEqualToExpression} from "./GreaterThanOrEqualToExpression";
import {EqualToExpression} from "./EqualToExpression";
import {AnyExpression} from "./AnyExpression";

// noinspection JSMethodCanBeStatic
export class ExpressionEngine {
	private debug = false;
	readonly isDebug: (expression: DynamicFormsExpression) => boolean;
	
	constructor(options: {
		debug?: boolean;
		isDebug?: (expression: DynamicFormsExpression) => boolean;
	} = {}) {
		this.isDebug = options.isDebug ?? (() => options.debug ?? Configuration.debug);
	}
	
	evaluate(expression: DynamicFormsExpression, context: RuntimeContext): RuntimeValue | null {
		const start = performance.now();
		let value = undefined;
		this.debug = this.isDebug(expression);
		try {
			if (this.debug) {
				console.group(expression.function);
				console.log(expression);
			}
			value = this.evaluateInternal(expression, context);
			return value;
		} catch (error) {
			if (this.debug)
				console.log(error);
			throw error;
		} finally {
			const end = performance.now();
			if (this.debug) {
				const duration = Math.ceil(end - start);
				console.log(`evaluating expression took ${duration} millisecond${duration === 1 ? "" : "s"}`, value);
				console.groupEnd();
			}
		}
	}
	
	private evaluateInternal(expression: DynamicFormsExpression, context: RuntimeContext): RuntimeValue | null {
		return this.grouped(expression?.function ?? "unknown", () => {
			switch (expression.function) {
				case "literal":
					return this.evaluateLiteral(expression as LiteralExpression);
				case "reference":
					return this.evaluateReference(expression as ReferenceExpression, context);
				case "if":
					return this.evaluateIf(expression as IfExpression, context);
				case "all":
					return this.evaluateAll(expression as AllExpression, context);
				case "not":
					return this.evaluateNot(expression as NotExpression, context);
				case "today":
					return this.evaluateToday(expression as TodayExpression, context);
				case "stringJoin":
					return this.evaluateStringJoin(expression as StringJoinExpression, context);
				case "greaterThanOrEqualTo":
					return this.evaluateGreaterThanOrEqualTo(expression as GreaterThanOrEqualToExpression, context);
				case "equalTo":
					return this.evaluateEqualTo(expression as EqualToExpression, context);
				case "any":
					return this.evaluateAny(expression as AnyExpression, context);
				default:
					console.error(`Unrecognized expression function '${expression.function}'.`);
					throw new Error(`Unrecognized expression function '${expression.function}'.`);
			}
		});
	}
	
	private evaluateLiteral(expression: LiteralExpression): RuntimeValue | null {
		if (this.debug)
			console.log(expression.value);
		
		return new RuntimeString(expression.value);
	}
	
	private evaluateReference(expression: ReferenceExpression, context: RuntimeContext): RuntimeValue | null {
		const value = context.getValueByReference(expression.name);
		if (this.debug)
			console.log("referenced value:", expression.name, value);
		return value;
	}
	
	private evaluateIf(expression: IfExpression, context: RuntimeContext): RuntimeValue | null {
		const condition = this.grouped("condition", () => this.evaluateInternal(expression.condition, context));
		
		if (condition === null)
			return null;
		
		if (condition.asBoolean()) {
			return this.grouped("then", () => this.evaluateInternal(expression.then, context));
		} else {
			return this.grouped("else", () => this.evaluateInternal(expression.else, context));
		}
	}
	
	private evaluateAll(expression: AllExpression, context: RuntimeContext): RuntimeValue | null {
		const values = expression.expr.map(it => this.evaluateInternal(it, context));
		
		if (values.some(it => it === null))
			return null;
		
		return new RuntimeBoolean(values.every(it => it?.asBoolean()));
	}
	
	private evaluateNot(expression: NotExpression, context: RuntimeContext): RuntimeValue | null {
		const value = this.evaluateInternal(expression.expr, context);
		
		if (value === null)
			return null;
		
		if (value.asBoolean()) {
			return new RuntimeBoolean(false);
		} else {
			return new RuntimeBoolean(true);
		}
	}
	
	private evaluateToday(expression: TodayExpression, context: RuntimeContext): RuntimeValue | null {
		const today = LocalDate.now();
		return new RuntimeString(today.toString());
	}
	
	private evaluateStringJoin(expression: StringJoinExpression, context: RuntimeContext): RuntimeValue | null {
		const values = this.grouped("values", () => expression.values.map(it => this.evaluateInternal(it, context)));
		const delimiter = expression.delimiter
			? this.grouped("delimiter", () => this.evaluateInternal(expression.delimiter as DynamicFormsExpression, context))?.asString()
			: ", ";
		
		if (delimiter === undefined || values.some(it => it === null))
			return null;

		const value = values.map(it => it?.asString()).join(delimiter);
		
		return new RuntimeString(value);
	}
	
	private evaluateGreaterThanOrEqualTo(expression: GreaterThanOrEqualToExpression, context: RuntimeContext): RuntimeValue | null {
		const left = this.grouped("left", () => this.evaluateInternal(expression.left, context));
		const right = this.grouped("right", () => this.evaluateInternal(expression.right, context));
		
		if (left === null || right === null)
			return null;
		
		const value = left.asNumber() >= right?.asNumber();
		
		return new RuntimeBoolean(value);
	}
	
	private evaluateEqualTo(expression: EqualToExpression, context: RuntimeContext): RuntimeValue | null {
		const left = this.grouped("left", () => this.evaluateInternal(expression.left, context));
		const right = this.grouped("right", () => this.evaluateInternal(expression.right, context));
		
		if (left === null || right === null)
			return null;
		
		const value = left.asString() === right?.asString();
		
		return new RuntimeBoolean(value);
	}
	
	private evaluateAny(expression: AnyExpression, context: RuntimeContext): RuntimeValue | null {
		const value = this.grouped("expr", () => expression.expr.some(it => {
			const x = this.evaluateInternal(it, context);
			return x !== null && x.asBoolean();
		}));
		
		return new RuntimeBoolean(value);
	}
	
	private grouped<T>(label: string, body: () => T): T {
		try {
			if (this.debug)
				console.group(label);
			return body();
		} finally {
			if (this.debug)
				console.groupEnd();
		}
	}
}
